/* eslint-disable ember/no-side-effects */
import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { PRISM_SPACING_500 } from '@mylighthouse/prism-foundation';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import { COLOR_DATA_PARITY_BENCHMARK } from 'frontend-utils/utils/css-variable';
import type * as Highcharts from 'highcharts';
import cloneDeep from 'lodash.clonedeep';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import type { ParityInsightHistoricalData } from 'lumen/models';
import { TOOLTIP_HOVER_TIMEOUT } from 'lumen/services/usage-analytics';
import { xValue } from 'lumen/utils/charts/chart-utils';
import renderTooltip from 'lumen/utils/charts/render-tooltip';
import {
  PARITY_COLORS,
  PREDICTED_PARITY_COLORS,
} from 'lumen/utils/constants/chart-color-constants';
import {
  HIGHCHARTS_MISSING_MARKER,
  HIGHCHARTS_SELECTED_MARKER,
} from 'lumen/utils/constants/general-constants';
import type {
  ChartCustomPoint,
  CustomSeries,
} from 'lumen/utils/constants/highcharts-constants';
import {
  BENCHMARK,
  DATE_AGGREGATION_MONTH,
  DATE_AGGREGATION_WEEK,
  type DateAggregation,
  LATEST,
  PARITY_FILTER,
  type ParityFilter,
  type ParityFilterOrBenchmark,
  type PercentagesWithTotal,
} from 'lumen/utils/constants/pi-constant-values';
import {
  calculateBenchmarkPercentages,
  calculateParityAnalyticsValues,
} from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import {
  getFormattedLastPossibleExtractDate,
  parityAnalyticsDateFormat,
} from 'lumen/utils/parity-analytics/date-utils';
import roundParityPercentages from 'lumen/utils/parity-analytics/round-parity-percentages';

type Marker =
  | typeof HIGHCHARTS_MISSING_MARKER
  | typeof HIGHCHARTS_SELECTED_MARKER;

type DataPoint = PercentagesWithTotal & { subscriptions?: number };

type GraphPoint = DataPoint & {
  crossed?: boolean;
  dashed?: boolean;
  isSelected?: boolean;
};

interface EvolutionPoint {
  defaultMarker: Marker | undefined;
  formattedDate: string;
  history: DataPoint;
  isLatest: boolean;
  isSelectable: boolean;
  marker: Marker | undefined;
  type: string;
  x: number;
  y: number;
}

interface BenchmarkPoint {
  isSelectable?: boolean;
  type: typeof BENCHMARK;
  x: number;
  y: number | null;
}

interface EvolutionSeriesData {
  data: EvolutionPoint[];
  zones: Highcharts.SeriesZonesOptionsObject[];
}

type EvolutionChartSeries =
  | (Omit<Highcharts.SeriesAreaOptions, 'data'> & { data: EvolutionPoint[] })
  | (Omit<Highcharts.SeriesLineOptions, 'data'> & { data: BenchmarkPoint[] });

interface BarValues {
  benchmark?: number;
  loss: { value: number };
  meet: { value: number };
  totalNbOfHotels: number | undefined;
  totalNbOfShops: number;
  win: { value: number };
}

interface EvolutionChartPoint extends ChartCustomPoint {
  formattedDate: string;
  history: DataPoint;
  isLatest: boolean;
  isSelectable: boolean;
  type: string;
}

interface EvolutionTooltipPoint
  extends Highcharts.TooltipFormatterContextObject {
  point: EvolutionChartPoint;
}

interface EvolutionTooltipContext
  extends Highcharts.TooltipFormatterContextObject {
  points?: EvolutionTooltipPoint[];
}

interface EvolutionAxis extends Highcharts.Axis {
  series: CustomSeries[];
}

interface EvolutionPointClickEvent extends Highcharts.PointClickEventObject {
  point: EvolutionChartPoint;
}

interface ParityChartsChartParityEvolutionSignature {
  Args: {
    customLegendItemClick: (filter: ParityFilterOrBenchmark) => void;
    data: ParityInsightHistoricalData | null | undefined;
    dateAggregation: DateAggregation;
    extractDate: string;
    hideHotelsMetric?: boolean;
    historicalExtractDate?: string;
    isBenchmarkVisible?: boolean;
    isLoading: boolean;
    parityFilters: Set<ParityFilter>;
    periodChanged: (period: Dayjs) => void;
  };
}

/**
 * Required
 * @param { Object } data The historical data to show in the chart
 * @param { String } dateAggregation week or month
 * @param { Boolean } isLoading Whether the data is still loading or not
 * Optional
 * @param { String } extractDate extractDate QP: marks the end of the selected period (week or month)
 * @param { String } historicalExtractDate historicalExtractdate QP: marks the end of the chart
 * @param { function } periodChanged Method called when clicking a point in the graph
 * @param { Set<String> } parityFilters Set of enabled filters (win, meet, loss)
 * @param { function } customLegendItemClick Method called when interacting with the chart legend
 */
export default class ParityChartsChartParityEvolution extends Component<ParityChartsChartParityEvolutionSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare dates: Services['dates'];
  // eslint-disable-next-line ember/no-unused-services
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  trackHoverTask = task(
    { restartable: true },
    async (formattedDate: string) => {
      await timeout(TOOLTIP_HOVER_TIMEOUT);
      this.usageAnalytics.track('Evolution Details Viewed', {
        eventTrigger: 'evolution-chart',
        periodType: this.args.dateAggregation,
        shopDate: formattedDate,
      });
    },
  );

  get chartData(): EvolutionChartSeries[] {
    if (this.args.isLoading) {
      return [];
    }

    const chartData: EvolutionChartSeries[] = [];
    const [loss, meet, win] = this.splitWinMeetLoss();
    const isWinVisible = this.args.parityFilters.has(PARITY_FILTER.WIN);
    const isMeetVisible = this.args.parityFilters.has(PARITY_FILTER.MEET);
    const isLossVisible = this.args.parityFilters.has(PARITY_FILTER.LOSS);
    const allFiltersEnabled = isLossVisible && isMeetVisible && isWinVisible;
    const allFiltersDisabled =
      !isLossVisible && !isMeetVisible && !isWinVisible;

    chartData.push({
      id: PARITY_FILTER.WIN,
      name: this.intl.t('parity_analytics.win').toString(),
      data: win.data,
      zoneAxis: 'x',
      zones: win.zones,
      type: 'area',
      fillColor: PARITY_COLORS[PARITY_FILTER.WIN].fillColor,
      color: PARITY_COLORS[PARITY_FILTER.WIN].color,
      zIndex: 10, // Required (otherwise areas don't overlap nicely)
      visible: isWinVisible,
      legendIndex: 3,
    });

    chartData.push({
      id: PARITY_FILTER.MEET,
      name: this.intl.t('parity_analytics.meet').toString(),
      data: meet.data,
      zoneAxis: 'x',
      zones: meet.zones,
      type: 'area',
      fillColor: PARITY_COLORS[PARITY_FILTER.MEET].fillColor,
      color: PARITY_COLORS[PARITY_FILTER.MEET].color,
      zIndex: 10,
      visible: isMeetVisible,
      legendIndex: 2,
    });

    chartData.push({
      id: PARITY_FILTER.LOSS,
      name: this.intl.t('parity_analytics.loss').toString(),
      data: loss.data,
      zoneAxis: 'x',
      zones: loss.zones,
      type: 'area',
      fillColor: PARITY_COLORS[PARITY_FILTER.LOSS].fillColor,
      color: PARITY_COLORS[PARITY_FILTER.LOSS].color,
      zIndex: 10,
      visible: isLossVisible,
      legendIndex: 1,
    });

    const benchmark = this.calculateBenchmark();
    chartData.push({
      id: BENCHMARK,
      name: this.intl.t('demand.benchmark').toString(),
      type: 'line',
      data: benchmark.data,
      zones: benchmark.zones,
      zoneAxis: 'x',
      color: `var(${COLOR_DATA_PARITY_BENCHMARK})`,
      zIndex: 11,
      visible:
        this.args.isBenchmarkVisible &&
        !(allFiltersEnabled || allFiltersDisabled),
      legendIndex: 4,
      marker: {
        enabled: false,
        states: {
          hover: { enabled: false },
          select: { enabled: false },
        },
      },
    });

    return chartData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      chart: {
        type: 'area',
        height: '346px',
        spacingTop: PRISM_SPACING_500,
        spacingBottom: 0,
        spacingLeft: PRISM_SPACING_500,
        spacingRight: PRISM_SPACING_500,
        zoomType: null,
      },
      title: {
        text: 'parity-evolution-chart',
      },
      tooltip: {
        positioner: null,
        shared: true,
        hideDelay: 0.2,
        shape: 'square',
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,
        distance: 40,

        formatter(this: EvolutionTooltipContext): string | false {
          const firstPoint = this.points?.[0];

          if (!firstPoint) {
            return false;
          }

          const { point } = firstPoint;
          const barValues: BarValues = {
            win: {
              value: point.history.win,
            },
            meet: {
              value: point.history.meet,
            },
            loss: {
              value: point.history.loss,
            },
            totalNbOfShops: point.history.totalNbOfShops,
            totalNbOfHotels: point.history.subscriptions,
          };

          const benchmarkPoint = this.points?.find(
            (p) => p.point.type === BENCHMARK,
          );
          if (benchmarkPoint && that.args.isBenchmarkVisible) {
            barValues.benchmark = benchmarkPoint.y;
          }

          let displayName = '';
          if (that.args.dateAggregation === DATE_AGGREGATION_WEEK) {
            if (point.isLatest) {
              displayName = that.intl.t('benchmark.current_score');
            } else {
              const formattedExtractDate = dayjs
                .unix(this.x)
                .utc()
                .format('ll');
              const formattedExtractDateEndOfWeek = dayjs
                .unix(this.x)
                .add(6, 'day')
                .utc()
                .format('ll');
              displayName = `${formattedExtractDate} - ${formattedExtractDateEndOfWeek}`;
            }
          } else if (that.args.dateAggregation === DATE_AGGREGATION_MONTH) {
            displayName = dayjs
              .unix(this.x)
              .utc()
              .format(that.dates.partialDateFormat.YEAR_AND_MONTH_LONG);
          }

          return renderTooltip(ParityTooltipsTooltipKpi, {
            owner: getOwner(that),
            args: {
              data: barValues,
              displayName,
              hideShoppedHotels: that.args.hideHotelsMetric,
            },
          });
        },
        useHTML: true,
      },
      xAxis: {
        allowDecimals: false,
        type: 'datetime',
        endOnTick: true,
        ordinal: false,
        tickPositioner(
          this: EvolutionAxis,
        ): Highcharts.AxisTickPositionsArray | null {
          if (this.series[0]) {
            const ticks: Highcharts.AxisTickPositionsArray =
              this.series[0].processedXData.slice();
            // @ts-expect-error: Object is possibly 'undefined'.
            ticks.info = this.tickPositions.info;
            return ticks;
          }
          return null;
        },
        labels: {
          rotation: -45,
          align: 'right',
          x: 10,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            if (this.value === xValue(LATEST)) {
              return that.intl.t('general.today').toString();
            }

            // Show the last day of the week
            if (that.args.dateAggregation === DATE_AGGREGATION_WEEK) {
              return parityAnalyticsDateFormat(
                // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number'.
                dayjs.unix(this.value).add(6, 'day').utc(),
                that.args.dateAggregation,
              );
            }

            return parityAnalyticsDateFormat(
              // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number'.
              dayjs.unix(this.value).utc(),
              that.args.dateAggregation,
            );
          },
        },
      },
      yAxis: [
        {
          endOnTick: true,
          ordinal: false,
          showFirstLabel: false,
          min: 0,
          max: 1,
          labels: {
            formatter(
              this: Highcharts.AxisLabelsFormatterContextObject,
            ): string {
              // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number | bigint | StringNumericLiteral | null | undefined'.
              return that.intl.formatNumber(this.value, { style: 'percent' });
            },
          },
          title: {
            text: '',
          },
        },
        {
          opposite: true,
          showFirstLabel: false,
          min: 0,
          title: {
            text: '',
          },
        },
      ],
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: true,
        borderWidth: 0,
        symbolWidth: 7,
        symbolHeight: 7,
        itemStyle: {
          width: 200,
          fontSize: '12px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      plotOptions: {
        area: {
          stacking: true,
          fillOpacity: 1,
          opacity: 1,
          point: {
            events: {
              mouseOver(this: EvolutionChartPoint): void {
                void that.trackHoverTask.perform(this.formattedDate);

                if (this.isSelectable) {
                  // We use parentGroup because otherwise the cursor only changes when hovering on the inside
                  // @ts-expect-error: Object is possibly 'undefined'. Property 'parentGroup' does not exist on type 'SVGElement'.
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  this.graphic.parentGroup.element.style.cursor = 'pointer';
                } else {
                  // @ts-expect-error: Object is possibly 'undefined'. Property 'parentGroup' does not exist on type 'SVGElement'.
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  this.graphic.parentGroup.element.style.cursor = 'auto';
                }
              },
              mouseOut(): void {
                void that.trackHoverTask.cancelAll();
              },
            },
          },
        },
        series: {
          events: {
            // Intercept legend actions (hide/show series)
            legendItemClick(
              this: Highcharts.Series,
              event: Highcharts.SeriesLegendItemClickEventObject,
            ): void {
              if (that.args.customLegendItemClick) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                event.preventDefault();
                // @ts-expect-error: Argument of type 'string | undefined' is not assignable to parameter of type 'ParityFilterOrBenchmark'.
                that.args.customLegendItemClick(this.options.id);
              }
            },
          },
          point: {
            events: {
              click(this: Highcharts.Point, e: EvolutionPointClickEvent): void {
                // Timestamp * 1000 for highcharts x-axis conversion to real unix timestamp
                const period = dayjs.utc(e.point.x * 1000);

                if (e.point.isSelectable) {
                  that.usageAnalytics.track('Date Changed', {
                    eventTrigger: 'evolution-chart',
                    periodType: that.args.dateAggregation,
                    startDaysOffsetFromToday: period.diff(
                      that.hotelAndMarketService.getLocalTime(),
                      'days',
                    ),
                  });

                  that.args.periodChanged(period);
                }
              },
            },
          },
        },
      },
    };
  }

  get isLatestSelected(): boolean {
    return (
      this.args.dateAggregation === DATE_AGGREGATION_WEEK &&
      dayjs(this.args.extractDate).isAfter(
        getFormattedLastPossibleExtractDate(),
      )
    );
  }

  get sortedKeys(): string[] {
    // @ts-expect-error: No overload matches this call.
    return Object.keys(this.args.data?.all).sort(
      (a, b) => xValue(a) - xValue(b),
    );
  }

  calculateBenchmark(): {
    data: BenchmarkPoint[];
    zones: Highcharts.SeriesZonesOptionsObject[];
  } {
    const zones: Highcharts.SeriesZonesOptionsObject[] = [];
    const data: BenchmarkPoint[] = [];

    let historicalDataIndex = 0;
    this.sortedKeys.forEach((date) => {
      // @ts-expect-error: Object is possibly 'undefined'.
      const point = this.args.data?.all[date][0];

      let benchmarkValues;
      if (point && point.benchmark) {
        benchmarkValues = point.benchmark;
      }

      const x = xValue(date);
      const dataPoint = roundParityPercentages(
        calculateBenchmarkPercentages(benchmarkValues),
      );
      const noShops = dataPoint.totalNbOfShops === 0;
      if (noShops) {
        data[historicalDataIndex] = {
          type: BENCHMARK,
          x,
          y: null,
        };
      } else {
        const graphPoint: GraphPoint = cloneDeep(dataPoint);

        if (date === LATEST) {
          this.markZoneAsDashed(graphPoint);
        }

        if (graphPoint.dashed) {
          zones.push({
            value: x,
            dashStyle: 'ShortDot',
          });
        } else {
          zones.push({
            value: x,
            dashStyle: 'Solid',
          });
        }

        this.args.parityFilters.forEach((key) => {
          if (!data[historicalDataIndex]) {
            data[historicalDataIndex] = {
              type: BENCHMARK,
              x,
              y: graphPoint[key],
              isSelectable: true,
            };
          } else {
            // @ts-expect-error: Object is possibly 'undefined'. Object is possibly 'null'.
            data[historicalDataIndex].y += graphPoint[key];
          }
        });
      }
      historicalDataIndex += 1;
    });

    return { data, zones };
  }

  markPointAsSelected(point: GraphPoint): void {
    point.isSelected = true;
  }

  markPointWithNoData(
    currentPoint: Record<string, unknown>,
    previousPoint: Record<string, unknown> | undefined,
  ): void {
    // Pad values with previous values (no pits in graph)
    Object.keys(currentPoint)
      .filter((key) => key !== 'totalNbOfShops')
      .forEach((key) => {
        if (previousPoint && previousPoint[key]) {
          currentPoint[key] = previousPoint[key];
        }
      });

    currentPoint.crossed = true;
  }

  markZoneAsDashed(point: GraphPoint): void {
    point.dashed = true;
  }

  splitWinMeetLoss(): [
    EvolutionSeriesData,
    EvolutionSeriesData,
    EvolutionSeriesData,
  ] {
    const previousPoint: { data?: GraphPoint; x?: number } = {};
    const winMeetLoss: Record<ParityFilter, EvolutionSeriesData> = {
      loss: { data: [], zones: [] },
      meet: { data: [], zones: [] },
      win: { data: [], zones: [] },
    };

    this.sortedKeys.forEach((date) => {
      // @ts-expect-error: Object is possibly 'undefined'.
      const point = this.args.data?.all[date][0];
      let pointValues;

      if (point) {
        pointValues = point.value;
      }

      const dataPoint: DataPoint = roundParityPercentages(
        calculateParityAnalyticsValues(pointValues),
      );

      // Add subscriptions count to the tooltip history point
      if (pointValues && pointValues.subscriptions) {
        dataPoint.subscriptions = pointValues.subscriptions;
      }

      // We make a copy, because we mutate the graph point when there is a no-data point present
      const graphPoint: GraphPoint = cloneDeep(dataPoint);

      const x = xValue(date);
      const isLatest = date === LATEST;
      if (isLatest) {
        this.markZoneAsDashed(graphPoint);
        // Mark Latest as selected when the extractDate (=today) is after the getFormattedLastPossibleExtractDate (=second last Monday)
        if (this.isLatestSelected) {
          this.markPointAsSelected(graphPoint);
        }
      }

      // If the selected extractDate (YYYY-MM-DD for weekly, YYYY-MM for monthly) is equal to this graph point, we want to mark it as selected
      else if (
        this.args.extractDate?.indexOf(date) > -1 &&
        !this.isLatestSelected
      ) {
        this.markPointAsSelected(graphPoint);
      }

      // Check if current dataPoint has number of shops
      if (graphPoint.totalNbOfShops === 0) {
        this.markPointWithNoData(graphPoint, previousPoint.data);
        this.markZoneAsDashed(graphPoint);
      }

      if (previousPoint.data && previousPoint.data.totalNbOfShops === 0) {
        this.markZoneAsDashed(graphPoint);
      }

      Object.keys(winMeetLoss).forEach((key) => {
        // Set up an undefined marker for this point to use
        let marker;

        // Set zones when current point has no shops or previous had no shops
        if (graphPoint.dashed) {
          // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Record<ParityFilter, EvolutionSeriesData>'.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          winMeetLoss[key].zones.push({
            value: x,
            dashStyle: 'Dash',
            // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ loss: ...; meet: ...; win: ... }'.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            fillColor: PREDICTED_PARITY_COLORS[key].fillColor,
            // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ loss: ...; meet: ...; win: ... }'.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            color: PREDICTED_PARITY_COLORS[key].color,
          });
        } else {
          // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Record<ParityFilter, EvolutionSeriesData>'.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          winMeetLoss[key].zones.push({
            value: x,
            dashStyle: 'Solid',
          });
        }

        if (graphPoint.isSelected) {
          marker = HIGHCHARTS_SELECTED_MARKER;
        }

        if (graphPoint.crossed) {
          marker = HIGHCHARTS_MISSING_MARKER;
        }

        // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Record<ParityFilter, EvolutionSeriesData>'.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        winMeetLoss[key].data.push({
          type: 'parity',
          x,
          formattedDate: date,
          // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'GraphPoint'.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: graphPoint[key],
          history: dataPoint,
          // Default marker for crossed points! They can be (de)selected, and ota-chart needs to know the default!
          defaultMarker: graphPoint.crossed ? marker : undefined,
          marker,
          // We don't want the user to select crossed datapoints (NOTE: Datepicker can however select them!)
          isSelectable: !graphPoint.crossed,
          isLatest,
        });
      });

      // Remember point for padding in future potential missing data
      previousPoint.x = x;
      previousPoint.data = graphPoint;
    });

    return [winMeetLoss.loss, winMeetLoss.meet, winMeetLoss.win];
  }

  <template>
    <PrismCard @contentLayout="noPadding">
      <:content>
        <GeneralChartsOtaChart
          {{! @glint-expect-error: Type '{ chart: { type: string; ...; zoomType: null; }; ... }' is not assignable to type 'Options'. }}
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
        />
      </:content>
    </PrismCard>
  </template>
}
